/**
 * AdminDashboardPage
 * Organization admin view for managing guests and sessions
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { DatePickerInput } from '@mantine/dates';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import { useAuthState } from '@/hooks/useAuthState';
import {
  signOut,
  changePassword,
  isEmailPasswordAuth,
} from '@/services/auth.service';
import {
  getSubscriptionStatus,
  getOrganization,
  updateOrganizationName,
  updateOrganizationLogo,
  removeOrganizationLogo,
  updateOrganizationPin,
  updateWeeklyRecapEnabled,
  updateWeeklyRecapRecipients,
  updateMirrorPreview,
} from '@/services/organization.service';
import {
  getQuestionnairesByOrg,
  createQuestionnaire,
  updateQuestionnaire,
  activateQuestionnaire,
} from '@/services/questionnaire.service';
import {
  getGuestsByOrg,
  getGuestSessions,
  getGuestSessionCount,
} from '@/services/guest.service';
import { Guest, Questionnaire, Question, QuestionType } from '@/types';
import type { PhotoSession } from '@/services/guest.service';
import { CustomSelect } from '@/components/CustomSelect';
import { GuestList, type GuestGroup } from '@/components/guests/GuestList';
import {
  formatDayHeader,
  formatResponseKey,
  formatResponseValue,
  getLocalDateKey,
} from '@/utils/guestFormat';
import './styles/AdminDashboardPage.css';

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [activeTab, setActiveTab] = useState<
    'guests' | 'questionnaire' | 'settings'
  >('guests');
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [expandedGuest, setExpandedGuest] = useState<string | null>(null);
  const [expandedSessionByGuestId, setExpandedSessionByGuestId] = useState<
    Record<string, string | null>
  >({});
  const [orgName, setOrgName] = useState('');
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  const [weeklyRecapEnabled, setWeeklyRecapEnabled] = useState(true);
  const [isSavingWeeklyRecap, setIsSavingWeeklyRecap] = useState(false);
  const [mirrorPreview, setMirrorPreview] = useState(true);
  const [isSavingMirrorPreview, setIsSavingMirrorPreview] = useState(false);
  const [recapRecipients, setRecapRecipients] = useState<string[]>([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [isSavingRecipients, setIsSavingRecipients] = useState(false);
  const [recipientsError, setRecipientsError] = useState('');
  const [recapEmail, setRecapEmail] = useState('');
  const [isSendingRecap, setIsSendingRecap] = useState(false);
  const [recapStatus, setRecapStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [adminPin, setAdminPin] = useState('');
  const [isEditingPin, setIsEditingPin] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Guests data
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestSessions, setGuestSessions] = useState<
    Record<string, PhotoSession[]>
  >({});
  const [sessionCountByGuestId, setSessionCountByGuestId] = useState<
    Record<string, number>
  >({});
  const [isLoadingGuests, setIsLoadingGuests] = useState(false);
  const [isLoadingSessionCounts, setIsLoadingSessionCounts] = useState(false);
  const [loadingSessionsForGuestId, setLoadingSessionsForGuestId] = useState<
    string | null
  >(null);
  const [sessionsErrorByGuestId, setSessionsErrorByGuestId] = useState<
    Record<string, string | null>
  >({});

  // Photo lightbox
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Email question is always included (for display purposes only - handled separately in QuestionnaireForm)
  const EMAIL_QUESTION: Question = {
    id: 'email',
    label: 'Email Address',
    type: 'email',
    required: true,
    order: 999, // Always last
  };

  // Questionnaire state
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<Questionnaire | null>(null);
  const [isEditingQuestionnaire, setIsEditingQuestionnaire] = useState(false);
  const [questionnaireTitle, setQuestionnaireTitle] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);

  // Filters
  const [emailFilter, setEmailFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const questionnairesById = useMemo(() => {
    const map: Record<string, Questionnaire> = {};
    for (const q of questionnaires) {
      map[q.id] = q;
    }
    return map;
  }, [questionnaires]);

  useEffect(() => {
    if (user) {
      loadSubscriptionStatus();
      loadQuestionnaires();
      loadOrganizationData();
      loadGuests();
    }
  }, [user]);

  useEffect(() => {
    if (!lightboxUrl) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxUrl(null);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxUrl]);

  const loadSubscriptionStatus = async () => {
    if (!user) return;
    const status = await getSubscriptionStatus(user.uid);
    setSubscriptionStatus(status);
  };

  const loadOrganizationData = async () => {
    if (!user) return;
    const org = await getOrganization(user.uid);
    if (org) {
      setOrgName(org.name || '');
      setOrgLogo(org.branding?.logoUrl || null);
      setAdminPin(org.adminPin || '');
      setWeeklyRecapEnabled(org.weeklyRecapEnabled !== false);
      setRecapRecipients(org.weeklyRecapRecipients || []);
      setMirrorPreview(org.mirrorPreview !== false);
    }
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const saveRecapRecipients = async (next: string[]) => {
    if (!user) return;
    const previous = recapRecipients;
    setRecapRecipients(next);
    setIsSavingRecipients(true);
    try {
      await updateWeeklyRecapRecipients(user.uid, next);
    } catch (error) {
      console.error('Error updating recap recipients:', error);
      setRecapRecipients(previous);
      setRecipientsError('Failed to save recipients. Please try again.');
    } finally {
      setIsSavingRecipients(false);
    }
  };

  const handleAddRecipient = async () => {
    const email = newRecipientEmail.trim();
    setRecipientsError('');
    if (!email) return;
    if (!isValidEmail(email)) {
      setRecipientsError('Please enter a valid email address.');
      return;
    }
    if (
      recapRecipients.some((r) => r.toLowerCase() === email.toLowerCase())
    ) {
      setRecipientsError('That email is already on the list.');
      return;
    }
    setNewRecipientEmail('');
    await saveRecapRecipients([...recapRecipients, email]);
  };

  const handleRemoveRecipient = async (email: string) => {
    setRecipientsError('');
    await saveRecapRecipients(recapRecipients.filter((r) => r !== email));
  };

  const handleSendRecap = async () => {
    const destination = recapEmail.trim();
    if (!destination) return;
    setIsSendingRecap(true);
    setRecapStatus(null);
    try {
      const fn = httpsCallable<
        { destinationEmail: string },
        { ok: boolean; sessionCount: number; weekLabel: string }
      >(functions, 'sendRecapEmail');
      const res = await fn({ destinationEmail: destination });
      setRecapStatus({
        type: 'success',
        message: `Sent — ${res.data.sessionCount} session${res.data.sessionCount === 1 ? '' : 's'} for ${res.data.weekLabel}.`,
      });
      setRecapEmail('');
    } catch (error) {
      console.error('Send recap failed:', error);
      setRecapStatus({
        type: 'error',
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSendingRecap(false);
    }
  };

  const handleToggleWeeklyRecap = async (enabled: boolean) => {
    if (!user) return;
    const previous = weeklyRecapEnabled;
    setWeeklyRecapEnabled(enabled);
    setIsSavingWeeklyRecap(true);
    try {
      await updateWeeklyRecapEnabled(user.uid, enabled);
    } catch (error) {
      console.error('Error updating weekly recap setting:', error);
      setWeeklyRecapEnabled(previous);
      alert('Failed to update weekly recap setting. Please try again.');
    } finally {
      setIsSavingWeeklyRecap(false);
    }
  };

  const handleToggleMirrorPreview = async (enabled: boolean) => {
    if (!user) return;
    const previous = mirrorPreview;
    setMirrorPreview(enabled);
    setIsSavingMirrorPreview(true);
    try {
      await updateMirrorPreview(user.uid, enabled);
    } catch (error) {
      console.error('Error updating mirror preview setting:', error);
      setMirrorPreview(previous);
      alert('Failed to update camera mirror setting. Please try again.');
    } finally {
      setIsSavingMirrorPreview(false);
    }
  };

  const loadGuests = async () => {
    if (!user) return;
    setIsLoadingGuests(true);
    try {
      const fetchedGuests = await getGuestsByOrg(user.uid);
      setGuests(fetchedGuests);

      setSessionCountByGuestId({});
      void loadSessionCountsForGuests(fetchedGuests);
    } catch (error) {
      console.error('Error loading guests:', error);
    } finally {
      setIsLoadingGuests(false);
    }
  };

  const loadSessionCountsForGuests = async (guestList: Guest[]) => {
    if (!user) return;

    setIsLoadingSessionCounts(true);
    try {
      const chunkSize = 10;
      for (let i = 0; i < guestList.length; i += chunkSize) {
        const chunk = guestList.slice(i, i + chunkSize);
        const results = await Promise.all(
          chunk.map(async (guest) => {
            try {
              const count = await getGuestSessionCount(guest.id, user.uid);
              return { guestId: guest.id, count };
            } catch (error) {
              console.error(
                `Error loading session count for guest ${guest.id}:`,
                error
              );
              return { guestId: guest.id, count: 0 };
            }
          })
        );

        const partial: Record<string, number> = {};
        for (const r of results) partial[r.guestId] = r.count;
        setSessionCountByGuestId((prev) => ({ ...prev, ...partial }));
      }
    } finally {
      setIsLoadingSessionCounts(false);
    }
  };

  const loadSessionsForGuest = async (guestId: string) => {
    if (!user) return;
    setLoadingSessionsForGuestId(guestId);
    setSessionsErrorByGuestId((prev) => ({ ...prev, [guestId]: null }));
    try {
      const sessions = await getGuestSessions(guestId, user.uid);
      setGuestSessions((prev) => ({
        ...prev,
        [guestId]: sessions,
      }));

      setExpandedSessionByGuestId((prev) => ({
        ...prev,
        [guestId]: sessions[0]?.id ?? null,
      }));
    } catch (error) {
      console.error('Error loading guest sessions:', error);
      setSessionsErrorByGuestId((prev) => ({
        ...prev,
        [guestId]: error instanceof Error ? error.message : String(error),
      }));
    } finally {
      setLoadingSessionsForGuestId((current) =>
        current === guestId ? null : current
      );
    }
  };

  const loadQuestionnaires = async () => {
    if (!user) return;
    const allQuestionnaires = await getQuestionnairesByOrg(user.uid);
    setQuestionnaires(allQuestionnaires);

    // Select active questionnaire or first one
    const active = allQuestionnaires.find((q) => q.isActive);
    if (active) {
      setSelectedQuestionnaire(active);
    } else if (allQuestionnaires.length > 0) {
      setSelectedQuestionnaire(allQuestionnaires[0]);
    }
  };

  const handleNewQuestionnaire = () => {
    setIsEditingQuestionnaire(true);
    setSelectedQuestionnaire(null);
    setQuestionnaireTitle('');
    setQuestions([EMAIL_QUESTION]); // Always include email question
  };

  const handleEditQuestionnaire = () => {
    if (!selectedQuestionnaire) return;
    setIsEditingQuestionnaire(true);
    setQuestionnaireTitle(selectedQuestionnaire.title);
    // Ensure email question is always included
    const hasEmailQuestion = selectedQuestionnaire.questions.some(
      (q) => q.id === 'email'
    );
    if (hasEmailQuestion) {
      setQuestions([...selectedQuestionnaire.questions]);
    } else {
      setQuestions([...selectedQuestionnaire.questions, EMAIL_QUESTION]);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingQuestionnaire(false);
    setQuestionnaireTitle('');
    setQuestions([]);

    // Restore selected questionnaire if user canceled "New Questionnaire"
    if (!selectedQuestionnaire && questionnaires.length > 0) {
      const active = questionnaires.find((q) => q.isActive);
      setSelectedQuestionnaire(active || questionnaires[0]);
    }
  };

  const handleSaveQuestionnaire = async () => {
    if (!user || !questionnaireTitle.trim()) return;

    try {
      let savedQuestionnaireId: string;

      if (selectedQuestionnaire) {
        // Update existing
        await updateQuestionnaire(
          selectedQuestionnaire.id,
          questionnaireTitle,
          questions
        );
        savedQuestionnaireId = selectedQuestionnaire.id;
      } else {
        // Create new
        const newId = await createQuestionnaire(
          user.uid,
          questionnaireTitle,
          questions
        );
        savedQuestionnaireId = newId;
      }

      const allQuestionnaires = await getQuestionnairesByOrg(user.uid);
      setQuestionnaires(allQuestionnaires);

      // Select the saved questionnaire
      const savedQuestionnaire = allQuestionnaires.find(
        (q) => q.id === savedQuestionnaireId
      );
      if (savedQuestionnaire) {
        setSelectedQuestionnaire(savedQuestionnaire);
      }

      setIsEditingQuestionnaire(false);
    } catch (error) {
      console.error('Error saving questionnaire:', error);
      alert('Failed to save questionnaire');
    }
  };

  const handleActivateQuestionnaire = async (questionnaireId: string) => {
    if (!user) return;

    try {
      await activateQuestionnaire(user.uid, questionnaireId);
      await loadQuestionnaires();
    } catch (error) {
      console.error('Error activating questionnaire:', error);
      alert('Failed to activate questionnaire');
    }
  };

  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: `q_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      order: questions.length,
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleUpdateQuestion = (
    index: number,
    field: keyof Question,
    value: any
  ) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleDeleteQuestion = (index: number) => {
    const questionToDelete = questions[index];
    // Prevent deleting the email question
    if (questionToDelete.id === 'email') {
      return;
    }
    const updated = questions.filter((_, i) => i !== index);
    // Reorder
    updated.forEach((q, i) => {
      q.order = i;
    });
    setQuestions(updated);
  };

  const handleMoveQuestion = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === questions.length - 1)
    ) {
      return;
    }

    const updated = [...questions];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];

    // Update order
    updated.forEach((q, i) => {
      q.order = i;
    });

    setQuestions(updated);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleExportCSV = async (
    timeframe: 'week' | 'month' | 'year' | 'all'
  ) => {
    setShowExportMenu(false);

    if (!user) return;

    // Check if UI filters are active
    const hasUIFilters =
      emailFilter.trim() !== '' || dateFrom !== null || dateTo !== null;

    // Calculate date range based on timeframe
    let fromDate: Date | null = null;
    const now = new Date();

    switch (timeframe) {
      case 'week':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        fromDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'all':
        fromDate = null;
        break;
    }

    // Collect all sessions from all guests
    const allSessionsData: Array<{
      email: string;
      sessionDate: Date;
      responses: Record<string, any>;
      questionnaireId?: string;
    }> = [];

    try {
      // Use filteredGuests instead of all guests to respect UI filters
      const guestsToExport = hasUIFilters ? filteredGuests : guests;

      // Load sessions for all guests
      for (const guest of guestsToExport) {
        let sessions = guestSessions[guest.id];

        // If sessions not already loaded, load them
        if (!sessions) {
          sessions = await getGuestSessions(guest.id, user.uid);
        }

        // Filter sessions by timeframe and collect data
        for (const session of sessions) {
          const sessionDate = session.createdAt?.toDate?.();
          if (!sessionDate) continue;

          // Apply timeframe filter
          if (fromDate && sessionDate < fromDate) continue;

          allSessionsData.push({
            email: guest.email,
            sessionDate,
            responses: session.responses || {},
            questionnaireId: session.questionnaireId,
          });
        }
      }

      if (allSessionsData.length === 0) {
        alert('No sessions found for the selected timeframe.');
        return;
      }

      // Get all unique question IDs across all sessions
      const allQuestionIds = new Set<string>();
      const questionLabels: Record<string, string> = {};

      allSessionsData.forEach((sessionData) => {
        Object.keys(sessionData.responses).forEach((questionId) => {
          allQuestionIds.add(questionId);

          // Get question label from questionnaire if available
          if (sessionData.questionnaireId) {
            const questionnaire =
              questionnairesById[sessionData.questionnaireId];
            const question = questionnaire?.questions.find(
              (q) => q.id === questionId
            );
            if (question && !questionLabels[questionId]) {
              questionLabels[questionId] = question.label;
            }
          }

          // Fallback to formatted key if no label found
          if (!questionLabels[questionId]) {
            questionLabels[questionId] = formatResponseKey(questionId);
          }
        });
      });

      const sortedQuestionIds = Array.from(allQuestionIds).sort();

      // Format date helper
      const formatDate = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
      };

      // Generate CSV headers
      const headers = [
        'Email',
        'Session Date',
        ...sortedQuestionIds.map((id) => questionLabels[id]),
      ];

      // Generate CSV rows
      const rows = allSessionsData.map((sessionData) => {
        return [
          sessionData.email,
          formatDate(sessionData.sessionDate),
          ...sortedQuestionIds.map((questionId) => {
            const value = sessionData.responses[questionId];
            return value !== undefined ? formatResponseValue(value) : '';
          }),
        ];
      });

      // Create CSV content with proper escaping
      const escapeCsvValue = (value: string) => {
        // Escape quotes and wrap in quotes if needed
        if (
          value.includes(',') ||
          value.includes('"') ||
          value.includes('\n')
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...rows.map((row) => row.map(escapeCsvValue).join(',')),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timeframeLabel =
        timeframe.charAt(0).toUpperCase() + timeframe.slice(1);
      const filename = `sundaybest_sessions_${timeframeLabel.toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const toggleGuestExpand = async (guestId: string) => {
    if (expandedGuest === guestId) {
      setExpandedGuest(null);
      return;
    }

    setExpandedGuest(guestId);
    const sessionsAlreadyLoaded = Boolean(guestSessions[guestId]);
    if (!sessionsAlreadyLoaded) {
      await loadSessionsForGuest(guestId);
    } else {
      const sessions = guestSessions[guestId] ?? [];
      setExpandedSessionByGuestId((prev) => ({
        ...prev,
        [guestId]: sessions[0]?.id ?? null,
      }));
    }
  };

  const selectSession = (guestId: string, sessionId: string) => {
    setExpandedSessionByGuestId((prev) => ({
      ...prev,
      [guestId]: sessionId,
    }));
  };

  const filteredGuests = guests.filter((guest) => {
    if (
      emailFilter &&
      !guest.email.toLowerCase().includes(emailFilter.toLowerCase())
    ) {
      return false;
    }

    const lastSessionDate = guest.lastSessionAt?.toDate?.();
    if (!lastSessionDate) {
      return true;
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      if (lastSessionDate < from) return false;
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (lastSessionDate > to) return false;
    }

    return true;
  });

  const allSessionCountsLoaded =
    filteredGuests.length === 0 ||
    filteredGuests.every((g) => sessionCountByGuestId[g.id] !== undefined);

  const totalSessionsForFilteredGuests = filteredGuests.reduce(
    (sum, guest) => sum + (sessionCountByGuestId[guest.id] ?? 0),
    0
  );

  const guestGroups = useMemo(() => {
    const groups: GuestGroup[] = [];
    const now = new Date();

    let currentKey: string | null = null;
    for (const guest of filteredGuests) {
      const d = guest.lastSessionAt?.toDate?.();
      const key = d ? getLocalDateKey(d) : 'no-sessions';
      if (key !== currentKey) {
        currentKey = key;
        const header = d
          ? formatDayHeader(d, now)
          : { primary: 'No sessions yet' };
        groups.push({
          key,
          label: header.primary,
          secondaryLabel: header.secondary,
          guests: [],
        });
      }

      groups[groups.length - 1].guests.push(guest);
    }

    return groups;
  }, [filteredGuests]);

  // Calculate weekly activity trend (last 8 weeks)
  const weeklyActivityTrend = useMemo(() => {
    const weeks = 8;
    const now = Date.now();
    const weekInMs = 7 * 24 * 60 * 60 * 1000;
    const counts = new Array(weeks).fill(0);

    filteredGuests.forEach((guest) => {
      const lastSessionDate = guest.lastSessionAt?.toDate?.();
      if (!lastSessionDate) return;

      const ageInMs = now - lastSessionDate.getTime();
      const weekIndex = Math.floor(ageInMs / weekInMs);

      if (weekIndex >= 0 && weekIndex < weeks) {
        counts[weeks - 1 - weekIndex] += 1;
      }
    });

    return counts;
  }, [filteredGuests]);

  const handleSaveOrgName = async () => {
    if (!user || !orgName.trim()) return;

    try {
      await updateOrganizationName(user.uid, orgName);
      setIsEditingOrgName(false);
    } catch (error) {
      console.error('Error saving organization name:', error);
      alert('Failed to save organization name');
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!user) return;

    try {
      const logoUrl = await updateOrganizationLogo(user.uid, file);
      setOrgLogo(logoUrl);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
    }
  };

  const handleRemoveLogo = async () => {
    if (!user) return;

    try {
      await removeOrganizationLogo(user.uid);
      setOrgLogo(null);
    } catch (error) {
      console.error('Error removing logo:', error);
      alert('Failed to remove logo');
    }
  };

  const handleSavePin = async () => {
    if (!user || !adminPin.trim()) return;

    // Validate PIN is 5 digits
    if (!/^\d{5}$/.test(adminPin)) {
      alert('PIN must be exactly 5 digits');
      return;
    }

    try {
      await updateOrganizationPin(user.uid, adminPin);
      setIsEditingPin(false);
    } catch (error) {
      console.error('Error saving PIN:', error);
      alert('Failed to save PIN');
    }
  };

  const handleChangePassword = async () => {
    if (!user) return;

    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setIsEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear success message after 3 seconds
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak');
      } else {
        setPasswordError(error.message || 'Failed to change password');
      }
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <button
          onClick={() => navigate('/booth')}
          className="return-booth-button"
        >
          ← Return to Photobooth
        </button>

        <img
          src="/assets/images/logo-long-white.png"
          alt="Sunday Best"
          className="admin-logo"
        />
      </header>

      {/* Main Content */}
      <div className="admin-content">
        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'guests' ? 'active' : ''}`}
            onClick={() => setActiveTab('guests')}
          >
            Guests
          </button>
          <button
            className={`tab-button ${activeTab === 'questionnaire' ? 'active' : ''}`}
            onClick={() => setActiveTab('questionnaire')}
          >
            Questionnaire
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Profile Settings
          </button>
        </div>

        {/* Guests Tab */}
        {activeTab === 'guests' && (
          <div className="admin-panel">
            {/* Metrics */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-value">{filteredGuests.length}</div>
                <div className="metric-label">Total Guests</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">
                  {allSessionCountsLoaded
                    ? totalSessionsForFilteredGuests
                    : isLoadingSessionCounts
                      ? '…'
                      : '—'}
                </div>
                <div className="metric-label">Total Sessions</div>
              </div>
              {/* Activity Line Graph */}
              <div className="metric-card activity-card">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <div
                    className="metric-label"
                    style={{ marginBottom: '1rem' }}
                  >
                    Activity
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <div
                        className="metric-value"
                        style={{ fontSize: '2rem', marginBottom: '0.25rem' }}
                      >
                        {weeklyActivityTrend[weeklyActivityTrend.length - 1] ||
                          0}
                      </div>
                      <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                        this week
                      </div>
                    </div>
                    {(() => {
                      const trend = weeklyActivityTrend;
                      const width = 140;
                      const height = 80;
                      const pad = 8;
                      const maxY = Math.max(...trend, 1);
                      const stepX = (width - pad * 2) / (trend.length - 1);

                      const toPoint = (i: number, v: number) => {
                        const x = pad + i * stepX;
                        const y = pad + (height - pad * 2) * (1 - v / maxY);
                        return [x, y];
                      };

                      const points = trend.map((v, i) => toPoint(i, v));
                      const d = points.reduce(
                        (acc, [x, y], i) =>
                          i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`,
                        ''
                      );

                      return (
                        <svg
                          width={width}
                          height={height}
                          viewBox={`0 0 ${width} ${height}`}
                          style={{ marginRight: '-0.5rem' }}
                        >
                          <defs>
                            <linearGradient
                              id="activityGradient"
                              x1="0%"
                              y1="0%"
                              x2="100%"
                              y2="0%"
                            >
                              <stop offset="0%" stopColor="#e9c46a" />
                              <stop offset="50%" stopColor="#f4a261" />
                              <stop offset="100%" stopColor="#e76f51" />
                            </linearGradient>
                            <linearGradient
                              id="activityFill"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="rgba(233,196,106,0.25)"
                              />
                              <stop
                                offset="100%"
                                stopColor="rgba(233,196,106,0.0)"
                              />
                            </linearGradient>
                            <filter
                              id="activityGlow"
                              x="-50%"
                              y="-50%"
                              width="200%"
                              height="200%"
                            >
                              <feGaussianBlur
                                stdDeviation="2"
                                result="coloredBlur"
                              />
                              <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                              </feMerge>
                            </filter>
                          </defs>

                          <path
                            d={`${d} L ${points[points.length - 1][0]} ${height - pad} L ${points[0][0]} ${height - pad} Z`}
                            fill="url(#activityFill)"
                            opacity="0.6"
                          />

                          <motion.path
                            d={d}
                            fill="none"
                            stroke="url(#activityGradient)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            filter="url(#activityGlow)"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{
                              duration: 2,
                              delay: 0.3,
                              ease: 'easeInOut',
                            }}
                          />

                          <motion.circle
                            cx={points[points.length - 1][0]}
                            cy={points[points.length - 1][1]}
                            r="3"
                            fill="#e76f51"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.3, delay: 2.3 }}
                          />
                        </svg>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
              <div className="filters-row">
                <input
                  type="text"
                  placeholder="Filter by email..."
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  className="filter-input"
                />

                <DatePickerInput
                  placeholder="From"
                  value={dateFrom}
                  // @ts-expect-error Mantine v8 DatePickerInput onChange types don't match Date-state setters
                  onChange={setDateFrom}
                  valueFormat="M/D/YYYY"
                  styles={{
                    root: {
                      flex: 1,
                      minWidth: '120px',
                      maxWidth: '200px',
                    },
                    input: {
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&:focus': {
                        outline: 'none',
                        borderColor: '#e9c46a',
                        boxShadow: '0 0 0 3px rgba(233, 196, 106, 0.2)',
                      },
                    },
                  }}
                />

                <DatePickerInput
                  placeholder="To"
                  value={dateTo}
                  // @ts-expect-error Mantine v8 DatePickerInput onChange types don't match Date-state setters
                  onChange={setDateTo}
                  valueFormat="M/D/YYYY"
                  styles={{
                    root: {
                      flex: 1,
                      minWidth: '120px',
                      maxWidth: '200px',
                    },
                    input: {
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&:focus': {
                        outline: 'none',
                        borderColor: '#e9c46a',
                        boxShadow: '0 0 0 3px rgba(233, 196, 106, 0.2)',
                      },
                    },
                  }}
                />

                {(emailFilter.trim() !== '' ||
                  dateFrom !== null ||
                  dateTo !== null) && (
                  <button
                    onClick={() => {
                      setEmailFilter('');
                      setDateFrom(null);
                      setDateTo(null);
                    }}
                    className="export-button"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      width: '40px',
                      minWidth: '40px',
                      minHeight: '40px',
                      padding: '0',
                      fontSize: '18px',
                    }}
                    title="Clear Filters"
                  >
                    ✕
                  </button>
                )}

                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="export-button"
                  >
                    {emailFilter.trim() !== '' ||
                    dateFrom !== null ||
                    dateTo !== null
                      ? 'Export CSV Range'
                      : 'Export CSV'}
                  </button>
                  {showExportMenu && (
                    <>
                      <div
                        className="export-menu-overlay"
                        onClick={() => setShowExportMenu(false)}
                      />
                      <div className="export-menu">
                        <button
                          onClick={() => handleExportCSV('week')}
                          className="export-menu-item"
                        >
                          Export Week
                        </button>
                        <button
                          onClick={() => handleExportCSV('month')}
                          className="export-menu-item"
                        >
                          Export Month
                        </button>
                        <button
                          onClick={() => handleExportCSV('year')}
                          className="export-menu-item"
                        >
                          Export Year
                        </button>
                        <button
                          onClick={() => handleExportCSV('all')}
                          className="export-menu-item"
                        >
                          Export All
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Guest List */}
            <GuestList
              groups={guestGroups}
              isLoading={isLoadingGuests}
              expandedGuestId={expandedGuest}
              onToggleGuest={toggleGuestExpand}
              sessionsByGuestId={guestSessions}
              sessionCountByGuestId={sessionCountByGuestId}
              isLoadingSessionCounts={isLoadingSessionCounts}
              loadingSessionsForGuestId={loadingSessionsForGuestId}
              sessionsErrorByGuestId={sessionsErrorByGuestId}
              onRetrySessions={loadSessionsForGuest}
              selectedSessionByGuestId={expandedSessionByGuestId}
              onSelectSession={selectSession}
              questionnairesById={questionnairesById}
              onOpenPhoto={setLightboxUrl}
            />
          </div>
        )}

        {/* Questionnaire Tab */}
        {activeTab === 'questionnaire' && (
          <div className="admin-panel">
            {!isEditingQuestionnaire ? (
              <>
                {/* Questionnaire Selector and Actions */}
                <div className="questionnaire-header">
                  <div className="questionnaire-selector">
                    <label className="selector-label">
                      Select Questionnaire:
                    </label>
                    <div className="selector-with-action">
                      <CustomSelect
                        value={selectedQuestionnaire?.id || ''}
                        onChange={(value) => {
                          const q = questionnaires.find((q) => q.id === value);
                          setSelectedQuestionnaire(q || null);
                        }}
                        options={
                          questionnaires.length === 0
                            ? [{ value: '', label: 'No questionnaires' }]
                            : questionnaires.map((q) => ({
                                value: q.id,
                                label: `${q.title}${q.isActive ? ' (Active)' : ''}`,
                              }))
                        }
                        placeholder="Select questionnaire..."
                        className="questionnaire-select"
                        theme="dark"
                      />
                      {selectedQuestionnaire &&
                        !selectedQuestionnaire.isActive && (
                          <button
                            onClick={() =>
                              handleActivateQuestionnaire(
                                selectedQuestionnaire.id
                              )
                            }
                            className="btn-make-active"
                          >
                            Make Active
                          </button>
                        )}
                    </div>
                  </div>

                  <div className="questionnaire-actions">
                    <button
                      onClick={handleNewQuestionnaire}
                      className="btn-new-questionnaire"
                    >
                      + New Questionnaire
                    </button>
                  </div>
                </div>

                {/* Display Selected Questionnaire */}
                {selectedQuestionnaire ? (
                  <div className="questionnaire-display">
                    <div className="questionnaire-title-section">
                      <h3 className="questionnaire-title">
                        {selectedQuestionnaire.title}
                      </h3>
                      <div className="questionnaire-status">
                        {selectedQuestionnaire.isActive && (
                          <span className="status-badge active">
                            Currently Active
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="questions-list">
                      <h4 className="section-subtitle">
                        Questions ({selectedQuestionnaire.questions.length})
                      </h4>
                      {selectedQuestionnaire.questions
                        .sort((a, b) => a.order - b.order)
                        .map((question, index) => (
                          <div
                            key={question.id}
                            className="question-display-item"
                          >
                            <div className="question-number">{index + 1}</div>
                            <div className="question-content">
                              <div className="question-label-display">
                                {question.label}
                                {question.required && (
                                  <span className="required-badge">
                                    Required
                                  </span>
                                )}
                              </div>
                              <div className="question-type-display">
                                Type: {question.type}
                                {question.options &&
                                  question.options.length > 0 && (
                                    <span className="options-preview">
                                      {' '}
                                      • Options: {question.options.join(', ')}
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>

                    <button
                      onClick={handleEditQuestionnaire}
                      className="btn-edit-questionnaire"
                    >
                      Edit Questionnaire
                    </button>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No questionnaires found. Create one to get started!</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Questionnaire Editor */}
                <div className="questionnaire-editor">
                  <h3 className="editor-title">
                    {selectedQuestionnaire
                      ? 'Edit Questionnaire'
                      : 'New Questionnaire'}
                  </h3>

                  <div className="form-group-admin">
                    <label className="form-label-admin">
                      Questionnaire Title
                    </label>
                    <input
                      type="text"
                      value={questionnaireTitle}
                      onChange={(e) => setQuestionnaireTitle(e.target.value)}
                      placeholder="e.g., Sunday Service Questionnaire"
                      className="form-input-admin"
                    />
                  </div>

                  <div className="questions-editor">
                    <div className="questions-editor-header">
                      <h4 className="section-subtitle">Questions</h4>
                    </div>

                    {questions.length === 0 ? (
                      <div className="empty-questions">
                        <p>No questions yet. Click "Add Question" to start.</p>
                      </div>
                    ) : (
                      <div className="questions-list-editor">
                        {questions
                          .sort((a, b) => a.order - b.order)
                          .map((question, index) => (
                            <div
                              key={question.id}
                              className="question-editor-item"
                            >
                              <div className="question-editor-header">
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                  }}
                                >
                                  <span className="question-number">
                                    Q{index + 1}
                                  </span>
                                  {question.id === 'email' && (
                                    <span
                                      style={{
                                        fontSize: '0.85rem',
                                        color: '#fff',
                                        fontWeight: '500',
                                      }}
                                    >
                                      (Always included)
                                    </span>
                                  )}
                                </div>
                                <div className="question-controls">
                                  <button
                                    onClick={() =>
                                      handleMoveQuestion(index, 'up')
                                    }
                                    disabled={
                                      index === 0 || question.id === 'email'
                                    }
                                    className="btn-move"
                                    title="Move up"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleMoveQuestion(index, 'down')
                                    }
                                    disabled={
                                      index === questions.length - 1 ||
                                      question.id === 'email'
                                    }
                                    className="btn-move"
                                    title="Move down"
                                  >
                                    ↓
                                  </button>
                                  <button
                                    onClick={() => handleDeleteQuestion(index)}
                                    className="btn-delete-question"
                                    title="Delete"
                                    disabled={question.id === 'email'}
                                    style={{
                                      opacity:
                                        question.id === 'email' ? 0.3 : 1,
                                      cursor:
                                        question.id === 'email'
                                          ? 'not-allowed'
                                          : 'pointer',
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>

                              <div className="question-editor-fields">
                                <div className="form-group-admin">
                                  <label className="form-label-small">
                                    Question Label
                                  </label>
                                  <input
                                    type="text"
                                    value={question.label}
                                    onChange={(e) =>
                                      handleUpdateQuestion(
                                        index,
                                        'label',
                                        e.target.value
                                      )
                                    }
                                    placeholder="e.g., What is your age group?"
                                    className="form-input-small"
                                    disabled={question.id === 'email'}
                                  />
                                </div>

                                <div className="form-row">
                                  <div className="form-group-admin">
                                    <label className="form-label-small">
                                      Type
                                    </label>
                                    <CustomSelect
                                      value={question.type}
                                      onChange={(value) =>
                                        handleUpdateQuestion(
                                          index,
                                          'type',
                                          value as QuestionType
                                        )
                                      }
                                      options={[
                                        { value: 'text', label: 'Text' },
                                        {
                                          value: 'textarea',
                                          label: 'Text Area',
                                        },
                                        { value: 'email', label: 'Email' },
                                        { value: 'number', label: 'Number' },
                                        {
                                          value: 'select',
                                          label: 'Select (Dropdown)',
                                        },
                                        {
                                          value: 'radio',
                                          label: 'Radio Buttons',
                                        },
                                        {
                                          value: 'multi-select',
                                          label: 'Multi-Select',
                                        },
                                        { value: 'boolean', label: 'Yes/No' },
                                      ]}
                                      className="form-select-small"
                                      isDisabled={question.id === 'email'}
                                      theme="dark"
                                    />
                                  </div>

                                  <div className="form-group-checkbox">
                                    <label className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={question.required}
                                        onChange={(e) =>
                                          handleUpdateQuestion(
                                            index,
                                            'required',
                                            e.target.checked
                                          )
                                        }
                                        disabled={question.id === 'email'}
                                      />
                                      Required
                                    </label>
                                  </div>
                                </div>

                                {(question.type === 'select' ||
                                  question.type === 'radio' ||
                                  question.type === 'multi-select') && (
                                  <div className="form-group-admin">
                                    <label className="form-label-small">
                                      Options (comma-separated)
                                    </label>
                                    <input
                                      type="text"
                                      defaultValue={
                                        question.options?.join(', ') || ''
                                      }
                                      onBlur={(e) =>
                                        handleUpdateQuestion(
                                          index,
                                          'options',
                                          e.target.value
                                            .split(',')
                                            .map((o) => o.trim())
                                            .filter((o) => o)
                                        )
                                      }
                                      placeholder="Option 1, Option 2, Option 3"
                                      className="form-input-small"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}

                    <button
                      onClick={handleAddQuestion}
                      className="btn-add-question"
                    >
                      + Add Question
                    </button>
                  </div>

                  <div className="editor-actions">
                    <button onClick={handleCancelEdit} className="btn-cancel">
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveQuestionnaire}
                      className="btn-save"
                      disabled={
                        !questionnaireTitle.trim() || questions.length === 0
                      }
                    >
                      Save Questionnaire
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Profile Settings Tab */}
        {activeTab === 'settings' && (
          <div className="admin-panel">
            <h2>Profile Settings</h2>

            {/* Organization Info */}
            <div className="settings-section">
              <h3 className="section-title-admin">Organization</h3>

              {/* Logo Upload */}
              <div className="settings-group">
                <label className="settings-label">Organization Logo</label>
                <div className="logo-upload-container">
                  {orgLogo ? (
                    <div className="logo-preview">
                      <img
                        src={orgLogo}
                        alt="Organization Logo"
                        className="org-logo"
                      />
                      <button
                        onClick={handleRemoveLogo}
                        className="btn-remove-logo"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="logo-upload-placeholder">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleLogoUpload(file);
                          }
                        }}
                        className="logo-file-input"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="logo-upload-label"
                      >
                        <span className="upload-icon">📷</span>
                        <span>Click to upload logo</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Organization Name */}
              <div className="settings-group">
                <label className="settings-label">Organization Name</label>
                {isEditingOrgName ? (
                  <div className="name-edit-container">
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="Enter organization name"
                      className="form-input-admin"
                      autoFocus
                    />
                    <div className="name-edit-actions">
                      <button
                        onClick={handleSaveOrgName}
                        className="btn-save-name"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setIsEditingOrgName(false)}
                        className="btn-cancel-name"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="name-display-container">
                    <span className="org-name-display">
                      {orgName || 'Not set'}
                    </span>
                    <button
                      onClick={() => setIsEditingOrgName(true)}
                      className="btn-edit-name"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              {/* Admin PIN */}
              <div className="settings-group">
                <label className="settings-label">Admin PIN</label>
                {isEditingPin ? (
                  <div className="name-edit-container">
                    <input
                      type="text"
                      value={adminPin}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow digits and max 5 characters
                        if (/^\d{0,5}$/.test(value)) {
                          setAdminPin(value);
                        }
                      }}
                      placeholder="Enter 5-digit PIN"
                      className="form-input-admin"
                      maxLength={5}
                      autoFocus
                    />
                    <div className="name-edit-actions">
                      <button
                        onClick={handleSavePin}
                        className="btn-save-name"
                        disabled={adminPin.length !== 5}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingPin(false);
                          // Reset to original value
                          loadOrganizationData();
                        }}
                        className="btn-cancel-name"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="name-display-container">
                    <span className="org-name-display">{'•'.repeat(5)}</span>
                    <button
                      onClick={() => setIsEditingPin(true)}
                      className="btn-edit-name"
                    >
                      Change PIN
                    </button>
                  </div>
                )}
              </div>

              {/* Password Change (only for email/password auth) */}
              {user && isEmailPasswordAuth(user) && (
                <div className="settings-group">
                  <label className="settings-label">Password</label>
                  {isEditingPassword ? (
                    <div className="name-edit-container">
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Current password"
                        className="form-input-admin"
                        autoFocus
                      />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New password (min 6 characters)"
                        className="form-input-admin"
                        style={{ marginTop: '0.5rem' }}
                      />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="form-input-admin"
                        style={{ marginTop: '0.5rem' }}
                      />
                      {passwordError && (
                        <div
                          style={{
                            color: '#e76f51',
                            fontSize: '0.85rem',
                            marginTop: '0.5rem',
                          }}
                        >
                          {passwordError}
                        </div>
                      )}
                      <div className="name-edit-actions">
                        <button
                          onClick={handleChangePassword}
                          className="btn-save-name"
                        >
                          Change Password
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingPassword(false);
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                            setPasswordError('');
                          }}
                          className="btn-cancel-name"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="name-display-container">
                      <span className="org-name-display">{'•'.repeat(8)}</span>
                      <button
                        onClick={() => setIsEditingPassword(true)}
                        className="btn-edit-name"
                      >
                        Change Password
                      </button>
                    </div>
                  )}
                  {passwordSuccess && (
                    <div
                      style={{
                        color: '#2a9d8f',
                        fontSize: '0.85rem',
                        marginTop: '0.5rem',
                      }}
                    >
                      {passwordSuccess}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Photo Booth */}
            <div className="settings-section">
              <h3 className="section-title-admin">Photo Booth</h3>
              <div className="settings-group">
                <div className="recap-toggle-row">
                  <div className="recap-toggle-text">
                    <div className="recap-toggle-title">Mirror camera</div>
                    <div className="recap-toggle-subtitle">
                      When enabled, the preview and photos are mirrored like a selfie. 
                      Turn off to display signs and text correctly.
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={mirrorPreview}
                      disabled={isSavingMirrorPreview}
                      onChange={(e) =>
                        handleToggleMirrorPreview(e.target.checked)
                      }
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>
            </div>

            {/* Weekly Recap Email */}
            <div className="settings-section">
              <h3 className="section-title-admin">Weekly Recap Email</h3>
              <div className="settings-group">
                <div className="recap-toggle-row">
                  <div className="recap-toggle-text">
                    <div className="recap-toggle-title">
                      Weekly recap email
                    </div>
                    <div className="recap-toggle-subtitle">
                      Every Monday — last week's sessions, guests, and a CSV
                      download.
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={weeklyRecapEnabled}
                      disabled={isSavingWeeklyRecap}
                      onChange={(e) =>
                        handleToggleWeeklyRecap(e.target.checked)
                      }
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <div className="settings-group">
                <label className="settings-label">Recipients</label>
                <div className="recap-toggle-subtitle recap-recipients-hint">
                  The recap goes to your account email plus everyone on this
                  list.
                </div>
                {recapRecipients.length > 0 && (
                  <ul className="recap-recipient-list">
                    {recapRecipients.map((email) => (
                      <li key={email} className="recap-recipient-row">
                        <span className="recap-recipient-email">{email}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(email)}
                          disabled={isSavingRecipients}
                          className="recap-recipient-remove"
                          aria-label={`Remove ${email}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="recap-send-row">
                  <input
                    type="email"
                    value={newRecipientEmail}
                    placeholder="teammate@example.com"
                    onChange={(e) => setNewRecipientEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddRecipient();
                      }
                    }}
                    disabled={isSavingRecipients}
                    className="recap-send-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddRecipient}
                    disabled={isSavingRecipients || !newRecipientEmail.trim()}
                    className="recap-send-btn"
                  >
                    {isSavingRecipients ? 'Saving…' : 'Add'}
                  </button>
                </div>
                {recipientsError && (
                  <p className="recap-status recap-status-error">
                    {recipientsError}
                  </p>
                )}
              </div>

              <div className="settings-group">
                <label className="settings-label">Send recap to email</label>
                <div className="recap-send-row">
                  <input
                    type="email"
                    value={recapEmail}
                    placeholder="email@example.com"
                    onChange={(e) => setRecapEmail(e.target.value)}
                    disabled={isSendingRecap}
                    className="recap-send-input"
                  />
                  <button
                    type="button"
                    onClick={handleSendRecap}
                    disabled={isSendingRecap || !recapEmail.trim()}
                    className="recap-send-btn"
                  >
                    {isSendingRecap ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {recapStatus && (
                  <p
                    className={`recap-status recap-status-${recapStatus.type}`}
                  >
                    {recapStatus.message}
                  </p>
                )}
              </div>
            </div>

            {/* Subscription Status */}
            <div className="settings-section">
              <h3 className="section-title-admin">Subscription</h3>
              {subscriptionStatus ? (
                <div className="subscription-info">
                  <div className="info-row">
                    <span className="info-label">Status:</span>
                    <span
                      className={`status-badge ${subscriptionStatus.status}`}
                    >
                      {subscriptionStatus.status}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Plan:</span>
                    <span>{subscriptionStatus.plan}</span>
                  </div>
                  {subscriptionStatus.currentPeriodEnd && (
                    <div className="info-row">
                      <span className="info-label">Renews:</span>
                      <span>
                        {subscriptionStatus.currentPeriodEnd
                          .toDate()
                          .toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="no-subscription">No active subscription</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="admin-footer">
        <button onClick={handleLogout} className="logout-button-admin">
          <span className="logout-icon">←</span>
          <span>Logout</span>
        </button>
      </footer>

      <AnimatePresence>
        {lightboxUrl && (
          <motion.div
            className="photo-lightbox"
            role="dialog"
            aria-modal="true"
            onClick={() => setLightboxUrl(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <button
              className="photo-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxUrl(null);
              }}
              aria-label="Close photo preview"
              type="button"
            >
              ×
            </button>

            <motion.img
              src={lightboxUrl}
              alt="Enlarged session photo"
              className="photo-lightbox-image"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
